import {
  ApiError,
  Comment,
  ConductEntry,
  PagedResponse,
  SchoolRelation,
  WithAvatar,
  useAddEntryCommentMutation,
  useEditEntryCommentMutation,
} from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

type CurrentStaff = SchoolRelation & WithAvatar;

type UseConductCommentsParams = {
  currentStaff?: CurrentStaff;
  queryKey: unknown[];
};

export const useConductComments = ({ currentStaff, queryKey }: UseConductCommentsParams) => {
  const queryClient = useQueryClient();
  const { showError } = useNotifications();
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;
  const [pendingEntriesId, setPendingEntriesId] = useState<Set<string>>(new Set());
  const addMutation = useAddEntryCommentMutation();
  const editMutation = useEditEntryCommentMutation();

  const addPendingEntry = useCallback((entryId: string) => {
    setPendingEntriesId((set) => {
      const next = new Set(set);
      next.add(entryId);
      return next;
    });
  }, []);

  const removePendingEntry = useCallback((entryId: string) => {
    setPendingEntriesId((set) => {
      const next = new Set(set);
      next.delete(entryId);
      return next;
    });
  }, []);

  const handleCommentAdd = useCallback(
    (entry: ConductEntry) => async (comment: string) => {
      if (!comment || !currentStaff) return;

      addPendingEntry(entry.id);

      try {
        const result = await addMutation.mutateAsync({
          entryId: entry.id,
          comment: { comment, creator_relation_id: currentStaff.relation_id },
        });

        const newComment: Comment = {
          comment,
          id: result.comment_id,
          creator_title: currentStaff.title,
          creator_relation_id: currentStaff.relation_id,
          creator_known_as: currentStaff.known_as,
          creator_last_name: currentStaff.last_name,
          creator_given_name: currentStaff.given_name,
        };

        queryClient.setQueryData(
          queryKeyRef.current,
          (data?: InfiniteData<PagedResponse<ConductEntry>>) =>
            data
              ? {
                  ...data,
                  pages: data.pages.map((page) => ({
                    ...page,
                    results: page.results.map((r) =>
                      r.id === entry.id ? { ...r, comments: [...r.comments, newComment] } : r,
                    ),
                  })),
                }
              : undefined,
        );
      } catch (e) {
        showError(e as ApiError);
      }

      removePendingEntry(entry.id);
    },
    [addMutation, addPendingEntry, currentStaff, queryClient, removePendingEntry, showError],
  );

  const handleCommentEdit = useCallback(
    (entry: ConductEntry) => async (comment: string, relationId: string) => {
      if (comment === undefined || !currentStaff) return;

      const existingComment = relationId
        ? entry.comments.find((c) => c.creator_relation_id === relationId)
        : undefined;

      if (!existingComment || existingComment.comment === comment) return;

      addPendingEntry(entry.id);

      try {
        await editMutation.mutateAsync({
          entryId: entry.id,
          commentId: existingComment.id,
          comment: { comment },
        });

        queryClient.setQueryData(
          queryKeyRef.current,
          (data?: InfiniteData<PagedResponse<ConductEntry>>) =>
            data
              ? {
                  ...data,
                  pages: data.pages.map((page) => ({
                    ...page,
                    results: page.results.map((r) =>
                      r.id === entry.id
                        ? {
                            ...r,
                            comments: comment
                              ? r.comments.map((c) =>
                                  c.id === existingComment.id ? { ...c, comment } : c,
                                )
                              : r.comments.filter((c) => c.id !== existingComment.id),
                          }
                        : r,
                    ),
                  })),
                }
              : undefined,
        );
      } catch (e) {
        showError(e as ApiError);
      }

      removePendingEntry(entry.id);
    },
    [editMutation, addPendingEntry, currentStaff, queryClient, removePendingEntry, showError],
  );

  return { handleCommentAdd, handleCommentEdit, pendingEntriesId };
};
